// A GUARDA DAS ROTAS DO ACERVO.
//
// Era um `useEffect` dentro do `Dashboard`, junto com tudo o mais. Quando o `/dashboard` virou
// quatro rotas (T2.45 do pedido), a guarda precisou virar uma peça própria — senão cada uma das
// quatro repetiria a mesma condição, e a quarta cópia é sempre a que alguém esquece de atualizar.
//
// ── O CONSERTO DO T2.17 / T2.18 / T2.19 MORA NESTA LINHA ─────────────────────────────────────
//
// Quando este arquivo nasceu, ele mandava para `/` quem não tem e-mail verificado — o
// comportamento do antigo `Dashboard`, copiado byte a byte de propósito, para não consertar um
// ponto de outro plano por acidente. Agora é a vez dele (`TS-T2.5.2`), e o conserto é aqui.
//
// TRÊS PONTOS, UMA LINHA. A janela do dossiê (`CONTEXTO-PL-T2.5.md`) mostra que ele estava
// DESLOGADO quando relatou os dois sintomas, e isso os une:
//
//   T2.17  "quando eu clico em criar quadrinho, eu continuo na área, na mesma página, nada
//           acontece"           <- visto DA HOME: vai para /meus-quadrinhos e volta para /
//   T2.19  "e se eu criar quadrinho, ele vai para home. Não faz sentido isso aqui."
//                               <- visto DA BIBLIOTECA: o mesmo salto, outro ponto de partida
//   T2.18  "não deveria ir para área de login para depois ir para parte de criar quadrinho"
//                               <- a frase sai com a negação invertida; o dossiê já marca isso,
//                                  e o sentido é o conserto: ir ao login e, DE LÁ, à criação
//
// Mandar para `/` é o pior dos destinos possíveis: some com a intenção da pessoa. Agora vai para
// `/login`, levando de onde saiu — e o login devolve para lá depois de entrar.
//
// UMA COISA QUE A CONDIÇÃO ESCONDE, e que vale dizer: ela testa `emailVerified`, não "está
// logado". Quem tem conta e não verificou o e-mail vive o mesmo salto. Por isso o login recebe
// também o MOTIVO, e diz qual dos dois é — antes, os dois casos eram silêncio.

import { ReactElement, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { GlobalState, useGlobalState } from "../../global-state-context";
import AppUser from "../../api/app-user.class";
import { paraOndeVoltar } from "../login/para-onde-voltar";

/**
 * Envolve uma rota do acervo. O filho só é chamado quando há usuário carregado, e recebe o
 * estado global já estreitado — sem isso, cada rota repetiria a mesma sequência de `if`s só
 * para o TypeScript aceitar que `appUser` existe.
 */
function GuardaDoAcervo({
  children,
}: {
  children: (ctx: { globalState: GlobalState; appUser: AppUser }) => ReactElement;
}) {
  const navigate = useNavigate();
  const local = useLocation();
  const globalState = useGlobalState();

  useEffect(() => {
    if (!globalState) return;
    if (globalState.authUser?.emailVerified) return;

    // T8.24 · o objeto saiu daqui para `para-onde-voltar.ts` no dia em que as outras duas telas de
    // conta passaram a precisar dele. Esta guarda fazia certo e era a unica: quem escreve o objeto na
    // mao esquece o `motivo`, e foi o que aconteceu — as outras duas passavam ZERO.
    navigate("/login", {
      replace: true,
      state: paraOndeVoltar(local, !!globalState.authUser),
    });
    // `local` inteiro na lista, e nao `local.pathname` e `local.search` separados: desde que o objeto
    // passou a ser montado por `paraOndeVoltar(local, ...)`, e ele que o efeito le. A lista antiga
    // continuaria correta na pratica — o objeto de `useLocation` troca junto com os dois campos —, mas
    // o `eslint` recusa o build, e a recusa dele esta certa: a lista tem de dizer o que o efeito le.
  }, [navigate, globalState, local]);

  if (!globalState?.appUser)
    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={170} style={{ color: "rgb(53, 161, 94)" }} />
      </div>
    );

  return children({ globalState, appUser: globalState.appUser });
}

export default GuardaDoAcervo;
